import { z } from "zod";
import { totpConfirmRequestSchema, totpConfirmResponseSchema, totpDisableRequestSchema, totpEnrollRequestSchema, totpEnrollResponseSchema, } from "./auth.js";
import { defineEndpoint } from "./endpoint.js";
import { platformSessionSchema } from "./platform-admin.js";
import { businessTypeSchema, signupVerificationSchema } from "./signup.js";
/**
 * Super Admin setup, settings, the admin's own two-step sign-in, and businesses created by the platform
 * (docs/specs/super-admin.md Part 2, docs/specs/signup.md Part 2; B-60b, B-63b).
 */
const tag = { tag: "platform-admin" };
const platform = { platformAdmin: true };
/** Platform routes an admin may use before enrolling TOTP when the platform requires it. */
const platformWithoutTotp = { platformAdmin: true, withoutTotp: true };
const name = z.string().trim().min(1).max(200);
const phoneInput = z.string().trim().min(10).max(32);
// First Super Admin --------------------------------------------------------------------------------------------
export const platformSetupStatusSchema = z.object({
    /** True only while no platform admin exists: the Super Admin shows "Create the first Super Admin". */
    open: z.boolean(),
});
export const platformSetupRequestSchema = z.object({
    name,
    email: z.email().max(254),
    phone: z.union([z.literal(""), phoneInput]).optional(),
    password: z.string().min(8).max(128),
});
export const getPlatformSetup = defineEndpoint({
    ...tag,
    name: "getPlatformSetup",
    method: "GET",
    path: "/v1/platform/setup",
    access: { public: true },
    summary: "Whether the first Super Admin can still be created",
    status: 200,
    response: platformSetupStatusSchema,
});
export const createFirstPlatformAdmin = defineEndpoint({
    ...tag,
    name: "createFirstPlatformAdmin",
    method: "POST",
    path: "/v1/platform/setup",
    access: { public: true },
    summary: "Create the first Super Admin (owner) and sign in; 404 once any admin exists",
    body: platformSetupRequestSchema,
    status: 201,
    response: platformSessionSchema,
    errors: ["EMAIL_REGISTERED", "PHONE_REGISTERED", "RATE_LIMITED"],
});
// Settings -----------------------------------------------------------------------------------------------------
export const platformSettingsSchema = z.object({
    signupVerification: signupVerificationSchema,
    superAdminTotpRequired: z.boolean(),
    /** An SMS gateway reaches people here (false: SMS signup codes cannot be delivered). */
    smsAvailable: z.boolean(),
    version: z.number().int(),
});
export const platformSettingsUpdateSchema = z.object({
    signupVerification: signupVerificationSchema.optional(),
    superAdminTotpRequired: z.boolean().optional(),
    version: z.number().int(),
});
export const getPlatformSettings = defineEndpoint({
    ...tag,
    name: "getPlatformSettings",
    method: "GET",
    path: "/v1/platform/settings",
    access: platform,
    summary: "Platform settings: signup code channel, Super Admin two-step sign-in",
    status: 200,
    response: platformSettingsSchema,
});
export const updatePlatformSettings = defineEndpoint({
    ...tag,
    name: "updatePlatformSettings",
    method: "PATCH",
    path: "/v1/platform/settings",
    access: platform,
    summary: "Change the platform settings (platform owners only; 409 on a stale version)",
    body: platformSettingsUpdateSchema,
    status: 200,
    response: platformSettingsSchema,
    errors: ["CONFLICT", "TOTP_NOT_ENROLLED"],
});
// The admin's own two-step sign-in ----------------------------------------------------------------------------
export const platformSecuritySchema = z.object({
    totpEnabled: z.boolean(),
    /** The platform requires two-step sign-in for every admin. */
    totpRequired: z.boolean(),
    /** Required but not enrolled: only enrolment works until it is done. */
    enrolRequired: z.boolean(),
});
export const getPlatformSecurity = defineEndpoint({
    ...tag,
    name: "getPlatformSecurity",
    method: "GET",
    path: "/v1/platform/me/security",
    access: platformWithoutTotp,
    summary: "The signed-in admin's two-step sign-in state",
    status: 200,
    response: platformSecuritySchema,
});
export const enrollPlatformTotp = defineEndpoint({
    ...tag,
    name: "enrollPlatformTotp",
    method: "POST",
    path: "/v1/platform/me/totp/enroll",
    access: platformWithoutTotp,
    summary: "Start two-step sign-in: re-enter the password, get the secret for the authenticator app",
    body: totpEnrollRequestSchema,
    status: 200,
    response: totpEnrollResponseSchema,
    errors: ["INVALID_CREDENTIALS", "CONFLICT", "RATE_LIMITED"],
});
export const confirmPlatformTotp = defineEndpoint({
    ...tag,
    name: "confirmPlatformTotp",
    method: "POST",
    path: "/v1/platform/me/totp/confirm",
    access: platformWithoutTotp,
    summary: "Turn two-step sign-in on with a code from the app; returns recovery codes once",
    body: totpConfirmRequestSchema,
    status: 200,
    response: totpConfirmResponseSchema,
    errors: ["INVALID_OTP", "RATE_LIMITED"],
});
export const disablePlatformTotp = defineEndpoint({
    ...tag,
    name: "disablePlatformTotp",
    method: "POST",
    path: "/v1/platform/me/totp/disable",
    access: platform,
    summary: "Turn own two-step sign-in off (not while the platform requires it)",
    body: totpDisableRequestSchema,
    status: 204,
    errors: ["INVALID_CREDENTIALS", "INVALID_OTP", "TOTP_NOT_ENROLLED", "RATE_LIMITED"],
});
// Businesses created by the platform ---------------------------------------------------------------------------
export const platformBusinessRequestSchema = z.object({
    businessName: name,
    businessType: businessTypeSchema,
    plannedBranches: z.number().int().min(1).max(500),
    ownerName: name,
    email: z.email().max(254),
    phone: phoneInput,
    locale: z.enum(["en", "bn"]).default("en"),
    /** Empty: the server makes one. Either way the owner must change it at first sign-in. */
    temporaryPassword: z.union([z.literal(""), z.string().min(8).max(128)]).optional(),
});
export const platformBusinessCreatedSchema = z.object({
    tenantId: z.uuid(),
    slug: z.string(),
    ownerUserId: z.uuid(),
    /** Shown once to the admin, to hand to the owner; never stored or sent in plain text by the platform. */
    temporaryPassword: z.string(),
});
export const createPlatformBusiness = defineEndpoint({
    ...tag,
    name: "createPlatformBusiness",
    method: "POST",
    path: "/v1/platform/tenants",
    access: platform,
    summary: "Create a business with its owner (trial on the default plan); the owner changes the password first",
    body: platformBusinessRequestSchema,
    status: 201,
    response: platformBusinessCreatedSchema,
    errors: ["EMAIL_REGISTERED", "PHONE_REGISTERED", "VALIDATION_FAILED"],
});
//# sourceMappingURL=platform-settings.js.map